<template>
  <v-container class="mt-10 profile">
    <!-- Cabeçalho do perfil e período analisado -->
    <v-card outlined class="mb-4">
      <v-card-text class="d-flex flex-wrap align-center">
        <v-avatar size="56" class="mr-4">
          <img v-if="avatar" :src="avatar" :alt="'Photo of ' + userName" />
          <v-icon v-else size="56">mdi-account-circle</v-icon>
        </v-avatar>
        <div class="flex-grow-1 my-2">
          <h1 class="text-h6 mb-0">{{ userName }}</h1>
          <div v-if="email && email !== userName" class="text-body-2 ink-secondary">{{ email }}</div>
        </div>
        <v-btn-toggle v-model="days" mandatory dense color="primary" class="my-2" aria-label="Period">
          <v-btn v-for="option in periods" :key="option" :value="option" small>{{ option }} days</v-btn>
        </v-btn-toggle>
        <v-btn icon class="ml-1" aria-label="Refresh" :loading="loading" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-text>
    </v-card>

    <v-alert v-if="error" type="warning" text dense>{{ error }}</v-alert>

    <!-- ===================== Minhas simulações ===================== -->
    <h2 class="text-subtitle-1 font-weight-medium mb-2">Your simulations</h2>
    <v-row dense class="mb-2">
      <v-col v-for="tile in userTiles" :key="tile.label" cols="6" md="3">
        <v-card outlined class="stat-tile">
          <v-card-text>
            <div class="text-caption ink-secondary">{{ tile.label }}</div>
            <div class="stat-value">{{ tile.value }}</div>
            <div class="text-caption ink-muted">{{ tile.note }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="!loading && !runs.length" outlined class="mb-4">
      <v-card-text class="text-center py-8">
        <v-icon large class="mb-2">mdi-chart-box-outline</v-icon>
        <div class="text-body-1">No simulations in the last {{ days }} days.</div>
        <div class="text-body-2 ink-secondary">
          Run a 2D or 3D simulation and its time, result and resources will show up here.
        </div>
      </v-card-text>
    </v-card>

    <template v-else-if="runs.length">
      <v-row dense>
        <v-col cols="12" md="6">
          <v-card outlined class="chart-card">
            <v-card-title class="text-subtitle-2">Simulations per day</v-card-title>
            <v-card-subtitle class="text-caption">Completed and failed runs</v-card-subtitle>
            <ResponsivePlot :data="perDayChart" :layout="layout({ barmode: 'stack', yTitle: 'Runs', counts: maxPerDay })" />
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="chart-card">
            <v-card-title class="text-subtitle-2">Time of each simulation</v-card-title>
            <v-card-subtitle class="text-caption">From clicking Run to the results, log scale</v-card-subtitle>
            <ResponsivePlot :data="durationChart" :layout="layout({ yTitle: 'Time (s)', yLog: true })" />
          </v-card>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col v-if="isAdmin" cols="12" md="5">
          <v-card outlined class="chart-card">
            <v-card-title class="text-subtitle-2">Where your simulations ran</v-card-title>
            <v-card-subtitle class="text-caption">
              Small simulations run on one server; large ones are split across the cluster nodes
            </v-card-subtitle>
            <ResponsivePlot :data="placementChart" :layout="layout({ horizontal: true, xTitle: 'Runs', counts: maxPerPlacement })" />
          </v-card>
        </v-col>
        <v-col cols="12" :md="isAdmin ? 7 : 12">
          <v-card outlined class="chart-card">
            <v-card-title class="text-subtitle-2">Results by type</v-card-title>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Type</th>
                  <th class="text-right">Runs</th>
                  <th class="text-right">Failed</th>
                  <th class="text-right">Median time</th>
                  <th class="text-right">Mean time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in kindRows" :key="row.kind">
                  <td><span class="swatch" :style="{ background: seriesColor(row.kind) }" />{{ kindLabel(row.kind) }}</td>
                  <td class="text-right num">{{ row.runs }}</td>
                  <td class="text-right num">{{ row.failed }}</td>
                  <td class="text-right num">{{ formatDuration(row.medianS) }}</td>
                  <td class="text-right num">{{ formatDuration(row.meanS) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-simple-table v-if="isAdmin" dense class="mt-2">
              <thead>
                <tr>
                  <th>Where</th>
                  <th class="text-right">Runs</th>
                  <th class="text-right">Median time</th>
                  <th class="text-right">Core-hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in placementRows" :key="row.name">
                  <td>{{ row.name }}</td>
                  <td class="text-right num">{{ row.runs }}</td>
                  <td class="text-right num">{{ formatDuration(row.medianS) }}</td>
                  <td class="text-right num">{{ row.cpuHours.toFixed(2) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="mt-2 mb-6">
        <v-card-title class="text-subtitle-2">Recent runs</v-card-title>
        <v-data-table
          :headers="runHeaders"
          :items="runs"
          :items-per-page="10"
          item-key="_id"
          dense
          class="runs-table"
        >
          <template v-slot:item.create_date="{ item }">{{ formatDate(item.create_date) }}</template>
          <template v-slot:item.title="{ item }">
            <router-link v-if="item.simulation" :to="simulationLink(item)">{{ item.title || "Untitled" }}</router-link>
            <span v-else>{{ item.title || "Untitled" }}</span>
          </template>
          <template v-slot:item.kind="{ item }">
            <span class="swatch" :style="{ background: seriesColor(item.kind) }" />{{ kindLabel(item.kind) }}
          </template>
          <template v-slot:item.status="{ item }">
            <span :title="item.error || ''" class="status">
              <v-icon small :color="item.status === 'completed' ? status.good : status.critical">
                {{ item.status === "completed" ? "mdi-check-circle" : "mdi-alert-circle" }}
              </v-icon>
              {{ item.status === "completed" ? "Completed" : "Failed" }}
            </span>
          </template>
          <template v-slot:item.durationS="{ item }">
            <span class="num">{{ formatDuration(item.durationS) }}</span>
          </template>
          <template v-slot:item.where="{ item }">{{ whereLabel(item) }}</template>
          <template v-slot:item.plan="{ item }">
            <v-chip v-if="planOf(item)" x-small outlined :title="planOf(item).reason || ''">
              {{ planOf(item).complexity === "complex" ? "Complex" : "Simple" }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <!-- ===================== Recursos do cluster ===================== -->
    <template v-if="isAdmin && usage">
    <h2 class="text-subtitle-1 font-weight-medium mb-2">Cluster resources <span class="text-caption ink-muted">(admin)</span></h2>
      <v-row dense class="mb-2">
        <v-col v-for="tile in clusterTiles" :key="tile.label" cols="6" md="3">
          <v-card outlined class="stat-tile">
            <v-card-text>
              <div class="text-caption ink-secondary">{{ tile.label }}</div>
              <div class="stat-value">{{ tile.value }}</div>
              <div class="text-caption ink-muted">{{ tile.note }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-card outlined class="chart-card">
            <v-card-title class="text-subtitle-2">Nodes</v-card-title>
            <v-card-subtitle class="text-caption">
              CPUs in use now, and the work each node did in the last {{ days }} days
            </v-card-subtitle>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Node</th>
                  <th>State</th>
                  <th style="min-width: 140px">CPUs in use</th>
                  <th class="text-right">Runs</th>
                  <th class="text-right">Core-hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="node in nodeRows" :key="node.name">
                  <td>{{ node.name }}</td>
                  <td>
                    <v-icon small :color="node.available ? status.good : status.critical">
                      {{ node.available ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ node.available ? "Available" : "Unavailable" }}
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <v-progress-linear
                        :value="node.cpus ? (100 * node.cpusAlloc) / node.cpus : 0"
                        height="6"
                        rounded
                        :color="sequential"
                        :background-color="track"
                        class="mr-2"
                        :aria-label="`${node.cpusAlloc} of ${node.cpus} CPUs in use`"
                      />
                      <span class="num text-caption">{{ node.cpusAlloc }}/{{ node.cpus }}</span>
                    </div>
                  </td>
                  <td class="text-right num">{{ node.runs }}</td>
                  <td class="text-right num">{{ node.cpuHours.toFixed(1) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="chart-card">
            <v-card-title class="text-subtitle-2">Cluster usage per day</v-card-title>
            <v-card-subtitle class="text-caption">Core-hours used by all simulations (all users)</v-card-subtitle>
            <ResponsivePlot :data="clusterDailyChart" :layout="layout({ yTitle: 'Core-hours' })" />
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="mt-2 mb-6">
        <v-card-title class="text-subtitle-2">Simple vs. complex simulations (all users)</v-card-title>
        <v-card-subtitle class="text-caption">
          Simple simulations run on one server with a few processes; complex ones use the available
          nodes in parallel. Queue is the wait for free nodes; solver is the computation itself.
        </v-card-subtitle>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Group</th>
              <th class="text-right">Runs</th>
              <th class="text-right">Failed</th>
              <th class="text-right">Mean queue</th>
              <th class="text-right">Mean solver</th>
              <th class="text-right">Median total</th>
              <th class="text-right">Core-hours</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in complexityRows" :key="group.label">
              <td>{{ group.label }}</td>
              <td class="text-right num">{{ group.runs }}</td>
              <td class="text-right num">{{ group.failed }}</td>
              <td class="text-right num">{{ formatDuration(group.avg_queue_s) }}</td>
              <td class="text-right num">{{ formatDuration(group.avg_solver_s) }}</td>
              <td class="text-right num">{{ formatDuration(group.median_total_s) }}</td>
              <td class="text-right num">{{ group.cpu_hours.toFixed(2) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import firebase from "firebase/app";
import ResponsivePlot from "@/components/ResponsivePlot";
import { mapGetters } from "vuex";
import runsApi from "@/services/runs";
import cluster from "@/services/cluster";
import { byKind, byPlacement, daily, formatDuration, placement, summarize } from "./stats";
import { role, loadRole, authHeaders } from "@/common/role";

// Paleta validada (scripts/validate_palette.js da skill de visualização): série 1 azul, série 2 laranja,
// cada modo com o seu passo; status com ícone e texto, nunca só a cor
const PALETTE = {
  light: { s1: "#2a78d6", s2: "#eb6834", ink: "#0b0b0b", ink2: "#52514e", muted: "#898781", grid: "#e1e0d9", axis: "#c3c2b7", track: "#e1e0d9" },
  dark: { s1: "#3987e5", s2: "#d95926", ink: "#f0efec", ink2: "#c3c2b7", muted: "#898781", grid: "#2c2c2a", axis: "#383835", track: "#383835" }
};
const STATUS = { good: "#0ca30c", critical: "#d03b3b" };
const REFRESH_MS = 30000;

export default {
  name: "Profile",
  components: { ResponsivePlot },
  data: () => ({
    role,
    days: 30,
    periods: [7, 30, 90],
    runs: [],
    usage: null,
    loading: false,
    error: "",
    timer: null,
    status: STATUS
  }),
  computed: {
    ...mapGetters("simulator", ["author"]),
    user() {
      return firebase.auth().currentUser;
    },
    userName() {
      return (this.user && (this.user.displayName || this.user.email)) || "User";
    },
    email() {
      return (this.user && this.user.email) || "";
    },
    avatar() {
      return (this.user && this.user.photoURL) || "";
    },
    colors() {
      return this.$vuetify.theme.dark ? PALETTE.dark : PALETTE.light;
    },
    sequential() {
      return this.colors.s1;
    },
    track() {
      return this.colors.track;
    },
    summary() {
      return summarize(this.runs);
    },
    isAdmin() {
      return this.role.isAdmin;
    },
    userTiles() {
      const s = this.summary;
      // Usuário comum: só o que é dele (sem recursos do cluster); admin: núcleo·horas e paralelo
      const last = this.isAdmin
        ? { label: "Compute used", value: `${s.cpuHours.toFixed(2)} core-h`, note: `${s.parallel} ran on 2+ nodes` }
        : { label: "Time simulating", value: formatDuration(s.totalS), note: "sum of all runs" };
      return [
        { label: "Simulations run", value: s.total, note: `last ${this.days} days` },
        {
          label: "Success rate",
          value: s.successRate === null ? "—" : `${Math.round(100 * s.successRate)}%`,
          note: `${s.failed} failed`
        },
        { label: "Median time", value: formatDuration(s.medianS), note: `longest ${formatDuration(s.longestS)}` },
        last
      ];
    },
    kindRows() {
      return byKind(this.runs);
    },
    placementRows() {
      return byPlacement(this.runs);
    },
    maxPerDay() {
      return Math.max(1, ...daily(this.runs, this.days).map(r => r.completed + r.failed));
    },
    maxPerPlacement() {
      return Math.max(1, ...this.placementRows.map(r => r.runs));
    },
    perDayChart() {
      const rows = daily(this.runs, this.days);
      const x = rows.map(r => r.date);
      return [
        {
          type: "bar", name: "Completed", x, y: rows.map(r => r.completed), marker: { color: STATUS.good },
          hovertemplate: "%{x|%b %d}<br>%{y} completed<extra></extra>"
        },
        {
          type: "bar", name: "Failed", x, y: rows.map(r => r.failed), marker: { color: STATUS.critical },
          hovertemplate: "%{x|%b %d}<br>%{y} failed<extra></extra>"
        }
      ];
    },
    durationChart() {
      return ["2d", "3d"].map(kind => {
        const items = this.runs.filter(r => r.kind === kind && Number.isFinite(r.durationS));
        return {
          type: "scatter",
          mode: "markers",
          name: this.kindLabel(kind),
          x: items.map(r => r.create_date),
          y: items.map(r => Math.max(r.durationS, 0.1)),
          text: items.map(r => `${r.title || "Untitled"} · ${placement(r)}${r.status === "failed" ? " · failed" : ""}`),
          marker: {
            size: 9,
            color: this.seriesColor(kind),
            symbol: items.map(r => (r.status === "failed" ? "x" : "circle")),
            line: { width: 2, color: this.$vuetify.theme.dark ? "#1a1a19" : "#fcfcfb" }
          },
          hovertemplate: "%{text}<br>%{x|%b %d %H:%M} · %{customdata}<extra></extra>",
          customdata: items.map(r => formatDuration(r.durationS))
        };
      });
    },
    placementChart() {
      const rows = this.placementRows;
      return [{
        type: "bar",
        orientation: "h",
        y: rows.map(r => r.name),
        x: rows.map(r => r.runs),
        marker: { color: this.colors.s1 },
        customdata: rows.map(r => formatDuration(r.medianS)),
        hovertemplate: "%{y}<br>%{x} runs · median %{customdata}<extra></extra>"
      }];
    },
    clusterTiles() {
      const u = this.usage;
      const c = u.cluster || {};
      return [
        {
          label: "Nodes available",
          value: c.reachable ? `${c.available_nodes} / ${c.total_nodes}` : "—",
          note: c.reachable ? "now" : "Slurm not reachable"
        },
        { label: "Free CPUs", value: c.reachable ? `${c.free_cpus} / ${c.total_cpus}` : "—", note: "on available nodes" },
        { label: "Jobs queued or running", value: u.active, note: `${u.runs} runs in ${u.days} days` },
        { label: "Cluster compute", value: `${u.cpu_hours.toFixed(1)} core-h`, note: `${u.failed} failed runs` }
      ];
    },
    nodeRows() {
      const usage = {};
      (this.usage.nodes || []).forEach(node => { usage[node.name] = node; });
      const live = ((this.usage.cluster || {}).nodes || []).map(node => ({
        name: node.name,
        available: node.available,
        cpus: node.cpus,
        cpusAlloc: node.cpus_alloc,
        runs: (usage[node.name] || {}).runs || 0,
        cpuHours: (usage[node.name] || {}).cpu_hours || 0
      }));
      // Nós que já rodaram jobs mas saíram da partição continuam na tabela
      Object.keys(usage).forEach(name => {
        if (!live.some(node => node.name === name)) {
          live.push({ name, available: false, cpus: 0, cpusAlloc: 0, runs: usage[name].runs, cpuHours: usage[name].cpu_hours });
        }
      });
      return live.sort((a, b) => a.name.localeCompare(b.name));
    },
    clusterDailyChart() {
      const days = this.usage.daily || [];
      return [{
        type: "bar",
        name: "Core-hours",
        x: days.map(d => d.date),
        y: days.map(d => d.cpu_hours),
        customdata: days.map(d => d.runs),
        marker: { color: this.colors.s1 },
        hovertemplate: "%{x|%b %d}<br>%{y:.2f} core-h · %{customdata} runs<extra></extra>"
      }];
    },
    complexityRows() {
      const labels = { simple: "Simple · 1 server", complex: "Complex · parallel", unknown: "Before the policy" };
      return (this.usage.by_complexity || []).map(group => ({ ...group, label: labels[group.key] || group.key }));
    },
    runHeaders() {
      const admin = this.isAdmin;
      return [
        { text: "When", value: "create_date" },
        { text: "Simulation", value: "title" },
        { text: "Type", value: "kind" },
        { text: "Result", value: "status" },
        { text: "Time", value: "durationS", align: "end" },
        ...(admin ? [{ text: "Where", value: "where", sortable: false }, { text: "Plan", value: "plan", sortable: false }] : [])
      ];
    }
  },
  watch: {
    days() {
      this.load();
    }
  },
  mounted() {
    document.title = "My profile - ElectrosFI";
    this.load();
    // Estado do cluster muda o tempo todo: atualiza enquanto a aba está visível
    this.timer = setInterval(() => {
      if (document.visibilityState === "visible") this.loadUsage();
    }, REFRESH_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    formatDuration,
    async load() {
      this.loading = true;
      this.error = "";
      await loadRole();
      await Promise.all([this.loadRuns(), this.loadUsage()]);
      this.loading = false;
    },
    async loadRuns() {
      const author = this.author || (this.user && this.user.uid);
      if (!author) return;
      try {
        const { data } = await runsApi.get(`/author/${encodeURIComponent(author)}`, { params: { days: this.days } });
        if (data.error) throw new Error(data.message);
        this.runs = data.data || [];
      } catch (err) {
        this.error = `Could not load your simulation history: ${err.message}`;
      }
    },
    async loadUsage() {
      // Uso do cluster: só administradores (o servidor confere o login)
      if (!this.role.isAdmin) {
        this.usage = null;
        return;
      }
      try {
        const { data } = await cluster.get("/usage", { params: { days: this.days }, headers: await authHeaders() });
        // Sem cluster o nginx devolve a página da interface (HTML) ou a rota falha
        this.usage = data && Array.isArray(data.by_complexity) ? data : null;
      } catch (err) {
        this.usage = null;
      }
    },
    layout({ barmode, yTitle, xTitle, yLog, horizontal, counts } = {}) {
      const c = this.colors;
      const axis = { color: c.muted, gridcolor: c.grid, linecolor: c.axis, zeroline: false, tickfont: { size: 11 } };
      // Contagens (counts = maior valor): só marcas inteiras no eixo do valor, nada de "1,5 execução"
      const valueAxis = counts ? { tickformat: "d", ...(counts <= 6 ? { dtick: 1 } : {}) } : {};
      return {
        height: 260,
        autosize: true,
        margin: { l: 48, r: 12, t: 8, b: 36 },
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        font: { family: "system-ui, -apple-system, 'Segoe UI', sans-serif", color: c.ink2, size: 12 },
        barmode,
        bargap: 0.25,
        showlegend: !horizontal,
        legend: { orientation: "h", x: 0, y: 1.12, font: { color: c.ink2 } },
        hoverlabel: { font: { family: "system-ui, sans-serif" } },
        xaxis: { ...axis, ...(horizontal ? valueAxis : {}), showgrid: !!horizontal,
          title: xTitle ? { text: xTitle, font: { size: 11 } } : undefined, type: horizontal ? "linear" : "date" },
        yaxis: { ...axis, rangemode: "tozero", ...(horizontal ? {} : valueAxis), showgrid: !horizontal,
          type: yLog ? "log" : horizontal ? "category" : "linear",
          title: yTitle ? { text: yTitle, font: { size: 11 } } : undefined, automargin: true }
      };
    },
    seriesColor(kind) {
      return kind === "3d" ? this.colors.s2 : this.colors.s1;
    },
    kindLabel(kind) {
      return kind === "3d" ? "3D (NGSolve)" : "2D (Meep)";
    },
    planOf(item) {
      return item.run && item.run.plan && item.run.plan.complexity ? item.run.plan : null;
    },
    whereLabel(item) {
      const info = item.run || {};
      if (info.mode !== "slurm") return "Local server";
      const nodes = info.numNodes || 1;
      const procs = info.ranks || 1;
      const threads = info.threads > 1 ? ` × ${info.threads} threads` : "";
      return `${nodes} node${nodes > 1 ? "s" : ""} · ${procs} process${procs > 1 ? "es" : ""}${threads}`;
    },
    simulationLink(item) {
      if (item.kind === "3d" && item.production) return `/datails3d/${item.simulation}/${item.production}/`;
      return `/${item.kind === "3d" ? "simulator3d" : "simulator"}/${item.simulation}`;
    },
    formatDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "—"
        : date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    }
  }
};
</script>

<style scoped>
.stat-tile .stat-value {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.3;
}
.ink-secondary {
  opacity: 0.75;
}
.ink-muted {
  opacity: 0.6;
}
.num {
  font-variant-numeric: tabular-nums;
}
.swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
  vertical-align: baseline;
}
.status {
  white-space: nowrap;
}
.chart-card {
  height: 100%;
  overflow: hidden;
}
</style>
